/**
 * Theme Configuration
 * Loads colors from environment variables and provides theme constants
 */

// Helper function to convert hex to RGB
const hexToRgb = (hex) => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  const r = Number.parseInt(hex.substring(0, 2), 16);
  const g = Number.parseInt(hex.substring(2, 4), 16);
  const b = Number.parseInt(hex.substring(4, 6), 16);
  
  return { r, g, b };
};

// Helper function to create rgba string
const hexToRgba = (hex, alpha = 1) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Lighten a hex color by mixing it with white (amount 0-1)
const lightenHex = (hex, amount = 0.25) => {
  const { r, g, b } = hexToRgb(hex);
  const mix = (channel) => Math.round(channel + (255 - channel) * amount);
  const toHex = (channel) => channel.toString(16).padStart(2, '0');
  return `${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
};

// Darken a hex color by mixing it with black (amount 0-1)
const darkenHex = (hex, amount = 0.25) => {
  const { r, g, b } = hexToRgb(hex);
  const mix = (channel) => Math.round(channel * (1 - amount));
  const toHex = (channel) => channel.toString(16).padStart(2, '0');
  return `${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
};

// "r, g, b" string for a hex color, for CSS custom properties consumed as
// rgba(var(--x-rgb), alpha)
const hexToRgbString = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  return `${r}, ${g}, ${b}`;
};

// Build the --phoenix-primary-* tint/shade ramp the Phoenix admin theme
// (theme.min.css) uses across buttons, badges, spinners, checkboxes, etc.
// so the dashboard and other authenticated screens pick up the tenant's
// brand color the same way the guest auth pages do.
const buildPhoenixPrimaryRamp = (primaryColor) => {
  const soft = lightenHex(primaryColor, 0.94);
  const c100 = lightenHex(primaryColor, 0.86);
  const c200 = lightenHex(primaryColor, 0.65);
  const c300 = lightenHex(primaryColor, 0.44);
  const c500 = darkenHex(primaryColor, 0.12);
  const c600 = darkenHex(primaryColor, 0.24);

  return {
    '--phoenix-primary': `#${primaryColor}`,
    '--phoenix-primary-rgb': hexToRgbString(primaryColor),
    '--phoenix-primary-soft': `#${soft}`,
    '--phoenix-primary-soft-rgb': hexToRgbString(soft),
    '--phoenix-primary-100': `#${c100}`,
    '--phoenix-primary-100-rgb': hexToRgbString(c100),
    '--phoenix-primary-200': `#${c200}`,
    '--phoenix-primary-300': `#${c300}`,
    '--phoenix-primary-300-rgb': hexToRgbString(c300),
    '--phoenix-primary-500': `#${c500}`,
    '--phoenix-primary-500-rgb': hexToRgbString(c500),
    '--phoenix-primary-600': `#${c600}`,
    '--phoenix-primary-600-rgb': hexToRgbString(c600),

    // Other root-level Phoenix vars that hardcode the template's default
    // blue instead of deriving from --phoenix-primary
    '--phoenix-link-color': `#${primaryColor}`,
    '--phoenix-link-hover-color': `#${c600}`,
    '--phoenix-navbar-vertical-link-active-color': `#${primaryColor}`,
    '--phoenix-avatar-name-color': `#${primaryColor}`,
  };
};

// Load colors from environment or use defaults (KWATI brand green).
// These are overridden at runtime by applyThemeToDocument() once the
// tenant's own brand color is fetched from the VAS getCompany API
// (the `colour` field returned by /vas/api/company/:company).
const PRIMARY_COLOR = process.env.REACT_APP_PRIMARY_COLOR || '056922';
const SECONDARY_COLOR = process.env.REACT_APP_SECONDARY_COLOR || '319944';

// Create full hex colors
const primaryHex = `#${PRIMARY_COLOR}`;
const secondaryHex = `#${SECONDARY_COLOR}`;

// Build a full color set from a single primary hex (no leading #), deriving
// a lighter secondary shade when one isn't explicitly supplied.
const buildColors = (primaryColor, secondaryColor = lightenHex(primaryColor, 0.25)) => ({
  primary: `#${primaryColor}`,
  secondary: `#${secondaryColor}`,

  // Primary variants
  primaryLight: hexToRgba(primaryColor, 0.1),
  primaryMedium: hexToRgba(primaryColor, 0.5),
  primaryDark: hexToRgba(primaryColor, 0.9),

  // Secondary variants
  secondaryLight: hexToRgba(secondaryColor, 0.1),
  secondaryMedium: hexToRgba(secondaryColor, 0.5),
  secondaryDark: hexToRgba(secondaryColor, 0.9),
});

// Theme object
const theme = {
  colors: {
    ...buildColors(PRIMARY_COLOR, SECONDARY_COLOR),

    // Semantic colors
    success: '#10B981',
    error: '#EF4444',
    warning: '#F59E0B',
    info: '#3B82F6',
    
    // Neutral colors
    white: '#FFFFFF',
    black: '#000000',
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
  },
  
  // Gradient helpers
  gradients: {
    primary: `linear-gradient(135deg, ${primaryHex} 0%, ${secondaryHex} 100%)`,
    primaryVertical: `linear-gradient(180deg, ${primaryHex} 0%, ${secondaryHex} 100%)`,
    primaryHorizontal: `linear-gradient(90deg, ${primaryHex} 0%, ${secondaryHex} 100%)`,
  },
  
  // Spacing
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem',
  },
  
  // Border radius
  borderRadius: {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  },
  
  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },
};

// Helper functions
export const getThemeColor = (colorPath) => {
  const paths = colorPath.split('.');
  let value = theme.colors;
  
  for (const path of paths) {
    value = value[path];
    if (!value) return null;
  }
  
  return value;
};

// Applies theme colors as CSS custom properties on <html>.
// Pass the tenant's brand color (the `colour` field from the VAS
// getCompany API, e.g. "#056922") to re-theme the app for that company;
// omit it to fall back to the REACT_APP_PRIMARY_COLOR/SECONDARY_COLOR
// defaults above.
export const applyThemeToDocument = (companyColor) => {
  const root = document.documentElement;

  const colors = companyColor
    ? buildColors(companyColor.replace('#', ''))
    : theme.colors;
  const gradient = `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`;

  // Set CSS custom properties
  root.style.setProperty('--color-primary', colors.primary);
  root.style.setProperty('--color-secondary', colors.secondary);
  root.style.setProperty('--color-primary-light', colors.primaryLight);
  root.style.setProperty('--color-primary-medium', colors.primaryMedium);
  root.style.setProperty('--color-primary-dark', colors.primaryDark);
  root.style.setProperty('--color-secondary-light', colors.secondaryLight);
  root.style.setProperty('--color-secondary-medium', colors.secondaryMedium);
  root.style.setProperty('--color-secondary-dark', colors.secondaryDark);
  root.style.setProperty('--gradient-primary', gradient);

  // Re-theme the Phoenix admin template (dashboard and other authenticated
  // screens) so it matches, since it reads its own --phoenix-primary-*
  // custom properties rather than the --color-primary ones above.
  const phoenixPrimary = buildPhoenixPrimaryRamp(colors.primary.replace('#', ''));
  Object.entries(phoenixPrimary).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
};

export default theme;
